import { Ring } from '@uiball/loaders'
import React, { useState } from 'react'
import { BrowserView, isMobile, MobileView } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { BackButton } from '../../components/BackButton'
import { CustomText } from '../../components/CustomText'
import { Loader } from '../../components/Loader'
import { Container, Content as ExContent } from '../../components/styled-components'
import Colors from '../../utils/Colors'
import { BASE_URL } from '../../utils/shared'
import { useContentDetails } from '../../hooks/useContentDetails'
import { useTaskAnswerDetails } from '../../hooks/useTaskAnswer'
import { TaskAnswerMediaResponseDto } from '../../new-api/api'

const Content = styled(ExContent)<{ isLoading: boolean }>`
  display: flex;
  justify-content: ${({ isLoading }) => (isLoading ? 'center' : 'flex-start')};
  flex-direction: column;
  border-radius: 6px;
  background-color: white;
  margin-top: 50px;
  padding-top: 40px;
  padding-bottom: 40px;
`

const InnerContent = styled.div`
  margin-left: 36px;
  margin-right: 36px;
  padding-top: ${isMobile ? 20 : 0}px;
  padding-left: ${isMobile ? 20 : 0}px;
  padding-right: ${isMobile ? 20 : 0}px;
`

const Section = styled.div`
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid ${Colors.border};
`

const MediaSection = styled.div`
  margin-top: 4px;
`

const MediaItem = styled.div`
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${Colors.border};

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`

const ClickableImage = styled.img`
  width: 100%;
  max-height: 300px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    opacity: 0.92;
  }
`

const VideoPlayer = styled.video`
  width: 100%;
  max-height: 400px;
  border-radius: 8px;
  background-color: ${Colors.lighterGray};
`

/* Lightbox */
const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`

const LightboxImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 4px;
`

const LightboxClose = styled.button`
  position: absolute;
  top: 20px;
  right: 24px;
  background: none;
  border: none;
  color: white;
  font-size: 32px;
  cursor: pointer;
  line-height: 1;
  padding: 0;
`

const LightboxNav = styled.button<{ side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ side }) => side}: 24px;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: white;
  font-size: 28px;
  cursor: pointer;
  padding: 8px 14px;
  border-radius: 4px;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`

export const TaskAnswerDetailsPage = () => {
  const { id } = useParams()
  const { t } = useTranslation()

  const { data: content, isLoading: isLoadingContent } = useContentDetails({
    contentId: Number(id),
  })
  const { data, isLoading: isLoadingAnswer } = useTaskAnswerDetails({
    contentId: Number(id),
  })

  const isLoading = isLoadingContent || isLoadingAnswer
  const answer = data?.answer

  const imageMedias = (answer?.medias || []).filter((m) => m.type?.startsWith('image'))

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const openLightbox = (media: TaskAnswerMediaResponseDto) => {
    const idx = imageMedias.findIndex((m) => m.id === media.id)
    if (idx !== -1) setLightboxIndex(idx)
  }

  const closeLightbox = () => setLightboxIndex(null)

  const prevImage = () =>
    setLightboxIndex((i) => (i !== null ? (i - 1 + imageMedias.length) % imageMedias.length : null))

  const nextImage = () => setLightboxIndex((i) => (i !== null ? (i + 1) % imageMedias.length : null))

  const innerContent = (
    <InnerContent>
      <CustomText fontSize={13} lineHeight={1.4} uppercase color={Colors.gray} style={{ marginBottom: 10 }}>
        {t('TASK')}
      </CustomText>
      <CustomText fontSize={20} lineHeight={1} fontWeight={500} color={Colors.darkGray} style={{ marginBottom: 24 }}>
        {content?.name || ''}
      </CustomText>

      {answer ? (
        <>
          {answer.group && (
            <Section>
              <CustomText fontSize={13} uppercase color={Colors.gray} lineHeight={1.4} style={{ marginBottom: 6 }}>
                {t('group')}
              </CustomText>
              <CustomText fontSize={16} fontWeight={500} lineHeight={1.4}>
                {answer.group.name}
              </CustomText>
            </Section>
          )}

          {answer.text && (
            <Section>
              <CustomText fontSize={13} uppercase color={Colors.gray} lineHeight={1.4} style={{ marginBottom: 6 }}>
                {t('submittedAnswer')}
              </CustomText>
              <CustomText fontSize={15} lineHeight={1.5}>
                {answer.text}
              </CustomText>
            </Section>
          )}

          {answer.medias && answer.medias.length > 0 && (
            <div>
              <CustomText fontSize={13} uppercase color={Colors.gray} lineHeight={1.4} style={{ marginBottom: 10 }}>
                {t('attachedMedia')}
              </CustomText>
              <MediaSection>
                {answer.medias.map((media) => (
                  <MediaItem key={media.id}>
                    {media.type?.startsWith('image') ? (
                      <ClickableImage
                        src={`${BASE_URL}/files/${media.url}`}
                        alt={media.name || 'media'}
                        crossOrigin="use-credentials"
                        onClick={() => openLightbox(media)}
                      />
                    ) : media.type?.startsWith('video') ? (
                      <VideoPlayer
                        src={`${BASE_URL}/files/${media.url}`}
                        controls
                        preload="metadata"
                        crossOrigin="use-credentials"
                      />
                    ) : null}
                    {media.name && (
                      <CustomText fontSize={12} color={Colors.gray} lineHeight={1.4} style={{ marginTop: 4 }}>
                        {media.name}
                      </CustomText>
                    )}
                  </MediaItem>
                ))}
              </MediaSection>
            </div>
          )}
        </>
      ) : (
        <CustomText fontSize={14} color={Colors.gray} lineHeight={1.4}>
          {t('noAnswerYet')}
        </CustomText>
      )}

      <div style={{ marginTop: 24 }}>
        <BackButton />
      </div>
    </InnerContent>
  )

  return (
    <>
      <Container style={{ backgroundColor: isMobile ? Colors.white : Colors.background }}>
        <BrowserView renderWithFragment>
          <Content isLoading={isLoading}>{isLoading ? <Loader /> : innerContent}</Content>
        </BrowserView>
        <MobileView>{isLoading ? <Ring color={Colors.primaryGreen} /> : innerContent}</MobileView>
      </Container>

      {lightboxIndex !== null && (
        <LightboxOverlay onClick={closeLightbox}>
          <LightboxClose onClick={closeLightbox}>×</LightboxClose>
          {imageMedias.length > 1 && (
            <LightboxNav
              side="left"
              onClick={(e) => {
                e.stopPropagation()
                prevImage()
              }}
            >
              ‹
            </LightboxNav>
          )}
          <LightboxImage
            src={`${BASE_URL}/files/${imageMedias[lightboxIndex].url}`}
            alt={imageMedias[lightboxIndex].name || 'media'}
            crossOrigin="use-credentials"
            onClick={(e) => e.stopPropagation()}
          />
          {imageMedias.length > 1 && (
            <LightboxNav
              side="right"
              onClick={(e) => {
                e.stopPropagation()
                nextImage()
              }}
            >
              ›
            </LightboxNav>
          )}
        </LightboxOverlay>
      )}
    </>
  )
}
